import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { connectAgencySocket, disconnectAgencySocket } from "../utils/socket";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const agencyData = JSON.parse(localStorage.getItem("agencyData") || "{}");
  const agencyId = agencyData?._id;
  const token = localStorage.getItem("token");

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );

  const mergeNotifications = (items) => {
    const map = new Map();

    items.forEach((item) => {
      if (item && item._id) {
        map.set(item._id, item);
      }
    });

    return Array.from(map.values());
  };

  const fetchNotifications = async () => {
    if (!agencyId || !token) return;

    setLoading(true);

    try {
      const res = await api.post("/api/website/get-agency-notifications", {
        agencyId,
      });

      const data = res.data?.data || [];

      setNotifications((prev) => mergeNotifications([...data, ...prev]));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!agencyId || !token) return;

    fetchNotifications();

    const socket = connectAgencySocket(agencyId);

    const onNewNotification = (notification) => {
      setNotifications((prev) =>
        mergeNotifications([notification, ...prev])
      );
    };

    // Prevent duplicate listeners
    socket?.off("agencyNotification", onNewNotification);
    socket?.on("agencyNotification", onNewNotification);

    return () => {
      socket?.off("agencyNotification", onNewNotification);

      // If your socket util is a singleton,
      // this is safe to keep.
      disconnectAgencySocket();
    };
  }, [agencyId, token]);

  const markAllRead = async () => {
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        isRead: true,
      }))
    );

    try {
      await api.post("/api/website/notifications/mark-all-read", {
        agencyId,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const markOneRead = async (id) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n._id === id
          ? {
              ...n,
              isRead: true,
            }
          : n
      )
    );

    try {
      await api.post("/api/website/notifications/mark-as-read", {
        agencyId,
        notificationId: id,
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAllRead,
        markOneRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);